import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { useI18n } from '@/i18n/i18n-provider';
import { authApi } from '@/services/auth.api';
import { Alert, Button, Field } from '@/components/ui';
import { LocalePicker } from '@/components/locale-picker';
import { LogoMark } from '@/components/logo-mark';

/**
 * Requests a reset link.
 *
 * Always shows the same confirmation, whether or not the email belongs to
 * an account — the API responds identically either way, and a page that
 * branched on the difference would undo the whole point of that design.
 */
export function ForgotPasswordPage() {
  const { t } = useI18n();

  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSent, setIsSent] = useState(false);

  function handleSubmit(event: FormEvent): void {
    event.preventDefault();
    void submit();
  }

  async function submit(): Promise<void> {
    setIsSubmitting(true);
    try {
      await authApi.requestPasswordReset(email);
    } finally {
      // Shown even if the request itself fails over the network: retrying
      // reveals nothing further, and a visible error here would be a signal
      // the design otherwise withholds.
      setIsSubmitting(false);
      setIsSent(true);
    }
  }

  return (
    <main className="auth-page">
      <div className="auth-card">
        <header className="auth-header">
          <LogoMark size={40} />
          <h1 className="auth-title">TABITA</h1>
          <p className="auth-subtitle">{t('auth.forgotPassword')}</p>
        </header>

        {isSent ? (
          <Alert tone="success">{t('auth.resetLinkSent')}</Alert>
        ) : (
          <form onSubmit={handleSubmit} noValidate className="auth-form">
            <Field
              label={t('auth.email')}
              name="email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
              autoComplete="username"
              autoFocus
              dir="ltr"
            />

            <Button type="submit" variant="primary" loading={isSubmitting}>
              {t('auth.sendResetLink')}
            </Button>
          </form>
        )}

        <footer className="auth-footer">
          <Link to="/login" className="back-link">
            {t('auth.backToSignIn')}
          </Link>
          <LocalePicker />
        </footer>
      </div>
    </main>
  );
}
